import classNames from 'classnames';
import type { ComponentProps, ReactNode, Ref } from 'react';
import type { To } from 'react-router';

import { NavLink } from '@sorare/routing';
import { css } from '@sorare/css';

import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconLock } from '#src/atoms/icons/system/iconLock.ts';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { Tooltip } from '#src/atoms/tooltip/Tooltip/index.tsx';
import { LabelM } from '#src/atoms/typography/index.tsx';
import { Badge } from '#src/atoms/ui/Badge/index.tsx';
import { LiveDot } from '#src/components/live/LiveDot/index.tsx';
import {
  type FlyTargetKey,
  useRegisterFlyTarget,
} from '#src/contexts/flyTarget/index.tsx';
import {
  useContextNavigation,
  useStoredContextNavigation,
} from '#src/hooks/useContextNavigation.ts';
import { breakpoints, laptopAndAbove } from '#src/style/mediaQuery.ts';

const root = css`
  justify-content: center;
  width: 100%;
  cursor: pointer;
  color: var(--c-onBase-secondary);

  &.locked {
    cursor: default;
    pointer-events: none;
  }

  &.horizontal {
    justify-content: flex-start;
    padding: var(--intermediate-unit);
    border-radius: var(--unit);

    &:not(.unactive).active {
      background-color: var(--c-surface-primary);
    }
    &:hover {
      background-color: rgba(var(--c-rgb-white), 0.04);
      color: var(--c-onBase-primary);
    }
    &.sidebarCollapsed {
      max-width: calc(5 * var(--unit));
      overflow: hidden;
    }
    &:not(.active).sidebarCollapsed:hover {
      transform: scale(1.2);
    }
    transition: 350ms transform var(--ease-out-slide);
  }
  &:not(.unactive) {
    &.active {
      color: var(--c-onBase-primary);
    }
  }
`;
const layout = css`
  position: relative;
  align-items: center;
  gap: var(--half-unit);
  justify-content: flex-start;
  width: 100%;

  .horizontal & {
    flex-direction: row;
    gap: var(--intermediate-unit);
  }
  @media ${laptopAndAbove} {
    :not(.active, .sidebarCollapsed):hover > & {
      transform: translateX(var(--half-unit));
    }
  }
  transition: 350ms transform var(--ease-out-slide);
`;
const relativeWrapper = css`
  position: relative;
`;
const iconWrapper = css`
  position: relative;
  min-width: var(--double-unit);
  min-height: var(--double-unit);

  .active.horizontal & {
    transform: scale(1.2);
  }
`;
const lockBadge = css`
  position: absolute;
  top: calc(-1 * var(--half-unit));
  right: calc(-1 * var(--half-unit));
  color: var(--c-onBase-primary);
`;
const fullWidthSBHorizontal = css`
  width: 100%;
  flex: 1;
  justify-content: center;

  .horizontal & {
    justify-content: space-between;
    white-space: nowrap;
  }
`;
const playLiveDotWrapper = css`
  position: absolute;
  top: calc(-1 * var(--unit));
  right: calc(-1 * var(--double-unit));
`;
const badgeClass = css`
  margin-right: 0;
  .horizontal & {
    margin-right: var(--unit);
  }
  .locked & {
    opacity: 0.2;
  }
`;
const navLabel = css`
  font: var(--t-label-xs);
  white-space: nowrap;
  @media (min-width: ${breakpoints.se}px) {
    font: var(--t-label-s);
  }
  .horizontal & {
    font: var(--t-label-l);
  }
  .locked & {
    opacity: 0.2;
  }
`;

const separator = css`
  width: 100%;
  height: 0px;
  border-top: 1px solid var(--c-ui-primary);
`;

export const Separator = () => <div className={separator} />;

type Props = {
  to?: To;
  icon?: ({ isActive }: { isActive: boolean }) => ReactNode;
  label: string;
  /** Rendered in normal flow after the label, unlike the absolutely-positioned `badge`. */
  labelSuffix?: ReactNode;
  controlledActive?: boolean;
  showLiveDot?: boolean;
  badge?: { content: ReactNode; ariaLabel?: string };
  variant?: 'horizontal' | 'vertical';
  sidebarCollapsed?: boolean;
  onClick?: () => void;
  keepSearchParams?: boolean;
  matches?: ComponentProps<typeof NavLink>['matches'];
  enableContextModeNavigation?: boolean;
  enabledStoredLastNavigation?: boolean;
  end?: ComponentProps<typeof NavLink>['end'];
  flyTargetKey?: FlyTargetKey;
  locked?: boolean;
};

const NavLinkContent = ({
  sidebarCollapsed,
  variant,
  label,
  labelSuffix,
  badge,
  icon,
  isActive,
  showLiveDot,
  iconRef,
  locked,
}: Pick<
  Props,
  | 'sidebarCollapsed'
  | 'showLiveDot'
  | 'variant'
  | 'label'
  | 'labelSuffix'
  | 'badge'
  | 'icon'
  | 'locked'
> & { isActive?: boolean; iconRef?: Ref<HTMLDivElement> }) => {
  const isDesktopLayout = variant === 'horizontal';
  return (
    <Vertical className={layout}>
      <Tooltip
        skip={!sidebarCollapsed || variant === 'vertical'}
        title={<LabelM>{label}</LabelM>}
        placement="right"
      >
        <Horizontal className={iconWrapper} center ref={iconRef}>
          <Badge
            className={badgeClass}
            invisible={
              !badge || (isDesktopLayout && !sidebarCollapsed) || locked
            }
            badgeContent={
              badge && (!isDesktopLayout || sidebarCollapsed)
                ? badge?.content
                : undefined
            }
            aria-label={
              badge && (!isDesktopLayout || sidebarCollapsed)
                ? badge?.ariaLabel
                : undefined
            }
          >
            {icon?.({ isActive: isActive ?? false })}
          </Badge>

          {!!locked && (
            <div className={lockBadge}>
              <FontAwesomeIcon icon={iconLock} size="xs" />
            </div>
          )}
        </Horizontal>
      </Tooltip>
      <Horizontal className={fullWidthSBHorizontal}>
        <Horizontal className={relativeWrapper}>
          <span className={classNames(navLabel, 'text-center')}>{label}</span>
          {!!showLiveDot && (
            <div className={playLiveDotWrapper}>
              <LiveDot animate size="md" />
            </div>
          )}
        </Horizontal>
        {labelSuffix}
        {!!(badge && isDesktopLayout) && (
          <Badge
            className={badgeClass}
            invisible={locked}
            badgeContent={badge?.content}
            aria-label={badge?.ariaLabel}
          />
        )}
      </Horizontal>
    </Vertical>
  );
};

export const NavItem = ({
  to,
  icon,
  label,
  labelSuffix,
  controlledActive,
  showLiveDot,
  badge,
  variant = 'horizontal',
  sidebarCollapsed,
  enableContextModeNavigation = false,
  enabledStoredLastNavigation = false,
  flyTargetKey,
  locked = false,
  ...rest
}: Props) => {
  const storedLastNav = useStoredContextNavigation({
    to: enabledStoredLastNavigation ? to : undefined,
  });
  const savedTo = useContextNavigation({
    to: enableContextModeNavigation ? to : undefined,
  });
  const computedTo = locked ? undefined : savedTo || storedLastNav || to;
  const flyTargetRef = useRegisterFlyTarget<HTMLDivElement>(flyTargetKey);

  if (computedTo) {
    return (
      <Horizontal
        as={NavLink}
        to={computedTo}
        state={{
          fromNavItem: !(savedTo || storedLastNav),
        }}
        className={classNames(root, {
          horizontal: variant === 'horizontal',
          active: controlledActive,
          unactive: controlledActive === false,
          sidebarCollapsed,
        })}
        {...rest}
      >
        {({ isActive }) => (
          <NavLinkContent
            sidebarCollapsed={sidebarCollapsed}
            variant={variant}
            label={label}
            labelSuffix={labelSuffix}
            badge={badge}
            icon={icon}
            isActive={
              controlledActive === undefined ? isActive : controlledActive
            }
            showLiveDot={showLiveDot}
            iconRef={flyTargetRef}
          />
        )}
      </Horizontal>
    );
  }

  return (
    <Horizontal
      className={classNames(root, {
        horizontal: variant === 'horizontal',
        active: controlledActive,
        unactive: controlledActive === false,
        sidebarCollapsed,
        locked,
      })}
      {...rest}
    >
      <NavLinkContent
        sidebarCollapsed={sidebarCollapsed}
        variant={variant}
        label={label}
        labelSuffix={labelSuffix}
        badge={badge}
        icon={icon}
        showLiveDot={showLiveDot}
        iconRef={flyTargetRef}
        locked={locked}
      />
    </Horizontal>
  );
};
